import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { authModule } from "../../api/modules/auth.module";
import { showToast } from "../../utils/toast";
import { setSession } from "../../auth/tokenStorage";
import { OtpView, EMPTY_CODE } from "./otp.view";

/**
 * `/otp-verification`, the container. The password-reset code.
 *
 * Everything visual is in `otp.view.jsx`, which `/email-verification-otp`
 * renders too. What is left here is this flow's own behaviour and nothing
 * else: which endpoint verifies the code, which endpoint resends it, and where
 * a verified user goes next.
 *
 * This screen is reached from the forgot-password flow, so the backend has
 * already set auth cookies by the time the code verifies. We cache the user
 * blob for UI state and continue to /reset-password with the code in hand.
 *
 * The page emits no class name from `OtpVerification.css` any more, and that
 * file is gone: `EmailVerificationOtp` was the only other importer and it is
 * converted in the same change.
 */

const RESEND_COOLDOWN_SECONDS = 60;

export default function OtpVerification() {
  const [otp, setOtp] = useState(EMPTY_CODE);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [focusSignal, setFocusSignal] = useState(0);

  const navigate = useNavigate();
  const location = useLocation();

  // Reached from the forgot-password flow only, which carries the address in
  // route state.
  const email = location.state?.email || "user@example.com";

  // Focus the first cell on mount, and again every time the code is cleared.
  useEffect(() => {
    setFocusSignal((signal) => signal + 1);
  }, []);

  const clearCode = useCallback(() => {
    setOtp(EMPTY_CODE);
    setFocusSignal((signal) => signal + 1);
  }, []);

  // Resend cooldown.
  useEffect(() => {
    if (resendTimer <= 0) return undefined;
    const interval = setInterval(() => {
      setResendTimer((timer) => timer - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [resendTimer]);

  const handleChange = (next) => {
    setOtp(next);
    // The error belongs to the code that produced it, so the first keystroke
    // of a new attempt retires it.
    if (error) setError("");
  };

  const handleSubmit = async (otpCode) => {
    if (otpCode.length !== 6) {
      showToast.error("Please enter all 6 digits");
      setError("Enter all 6 digits of the code.");
      return;
    }

    setLoading(true);

    try {
      const result = await authModule.verifyPasswordResetOtp({
        email: email,
        otp: otpCode,
      });

      if (result.success) {
        // The verify-otp endpoint sets auth cookies regardless of flow.
        // For password-reset we continue to the reset-password screen;
        // the session cookies are already in place so subsequent requests
        // are authenticated. We also cache the user blob for UI state.
        const details = result.data?.details;
        if (details) {
          const userData = {
            id: details.id,
            email: details.email,
            full_name: details.full_name,
            role: details.role,
          };
          setSession({ user: userData });
        }

        showToast.success(result.message);
        setSuccess(result.message || "Code verified. Taking you to your new password.");

        navigate("/reset-password", {
          state: {
            email: email,
            otp: otpCode,
            verified: true,
          },
        });
      } else {
        showToast.error(result.message);
        // The toast is transient and lands away from the field. The inline
        // message stays with the cells that are wrong.
        setError(result.message || "That code is not right. Check the latest email and try again.");
        clearCode();
      }
    } catch (err) {
      showToast.error("Something went wrong. Please try again.");
      setError("Something went wrong. Please try again.");
      console.error("OTP verification error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendTimer > 0) return;

    setResendLoading(true);

    try {
      const result = await authModule.requestPasswordResetOtp({
        email: email,
      });

      if (result.success) {
        showToast.success("New OTP sent to your email");
        setError("");
        clearCode();
        setResendTimer(RESEND_COOLDOWN_SECONDS);
      } else {
        showToast.error(result.message);
        setError(result.message || "We could not send a new code. Try again in a moment.");
      }
    } catch (err) {
      showToast.error("Failed to resend OTP. Please try again.");
      setError("We could not send a new code. Try again in a moment.");
      console.error("Resend OTP error:", err);
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <OtpView
      variant="password-reset"
      destination={email}
      values={otp}
      onChange={handleChange}
      onSubmit={handleSubmit}
      loading={loading}
      error={error}
      success={success}
      resendSeconds={resendTimer}
      resendLoading={resendLoading}
      onResend={handleResend}
      focusSignal={focusSignal}
    />
  );
}
